import { unserialize } from "./serializeLikePhp";
import urlMap from "@data/oldURLMap";
import { dataService } from "@thegetty-private/rad-app-utils";
import { getApiPath } from "@/helpers/configHelper";

// the W3C / google proposal for bookmarking with text-highlighting
// https://github.com/WICG/scroll-to-text-fragment
const BOOKMARK_PREFIX = ":~:";
/**
 * This callback type is called `next` and it is used to resolve
 * the beforeEnter hook.
 *
 * @callback next
 * @param {string|object|bool} - passing false aborts the current
 * navigation and passing a string or object of a new path redirects
 * to that location. (see https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards)
 */

/**
 * Function to handle all /search URLS before enter
 *
 * @param {object} to - the target route object being navigated to
 * @param {next} next - the callback function to resolve the
 * beforeEnter hook
 *
 * @return {array} result - an array of artist names
 */
export async function handleSearchURL(to, next) {
  const query = to.query;
  let decoded_query;
  let newQuery = {};
  if (query.query) {
    decoded_query = atob(query.query);
    newQuery = {
      ...(await translateOldQuery(decoded_query))
    };
    newQuery.view = query.view;
  }
  // handle legacy hard-coded provenance search
  if (query.pr) {
    let pr = query.pr;
    if (Array.isArray(pr) == false) {
      pr = [pr];
    }

    if (pr.includes("1") || pr.includes(1)) {
      newQuery.has_provenance = true;
    }

    if (pr.includes("3") || pr.includes(3)) {
      newQuery.nazi_era_gaps = true;
    }
    if (pr.includes("4") || pr.includes(4)) {
      newQuery.nazi_era_restitution = true;
    }
  }
  if (Object.keys(newQuery).length > 0) {
    next({ path: "/search", query: newQuery, route: "search" });
  } else {
    next();
  }
}

/**
 * Function to translate DOR IDs to artist names
 *
 * @param {array} dor_ids - array of dor_ids from the query (if it is
 *  just one it won't be an array, which we guard against with 14-16)
 *
 * @return {array} result - an array of artist names
 */
async function getArtistsByDORID(dor_ids) {
  let baseURL = getApiPath("/") + "api/artistlookup?";
  let prefix = "id=";
  dor_ids = dor_ids.map((x) => {
    return prefix + String(x) + "&";
  });
  let url = baseURL + dor_ids.join("") + "type=dor";
  const results = await dataService.axiosGet(url);
  if (results.data.skipped) {
    console.error(
      "These DOR IDs were not found in the index: ",
      results.data.skipped
    );
  }
  return results.data.names;
}

/**
 * Function to translate a query from an old CO URL
 *
 * @param {string} old_query - the base-64 encoded query from CO
 *
 * @return {object} toReturn - a query object in normal format
 */
export async function translateOldQuery(decoded_query) {
  let toReturn = { size: 18, from: 0 };
  let query = unserialize(decoded_query);
  for (let key in query) {
    //log every time we come across a value we cannot translate
    let newTermKey = urlMap[key]["new_term"];
    let typeKey = urlMap[key].type;
    let queryKey = query[key];
    if (typeKey == "unknown-value") {
      console.warn(
        "Had to ignore " +
          newTermKey +
          " because we don't yet have a map with which to translate the value we get from CO: " +
          queryKey
      );
    }
    if (typeKey == "artist-value") {
      queryKey = Array.isArray(queryKey) ? queryKey : [queryKey];
      toReturn[newTermKey] = await getArtistsByDORID(queryKey);
    }
    if (typeKey == "value") {
      toReturn[newTermKey] = queryKey;
    }
    if (typeKey == "bool") {
      toReturn[newTermKey] = true;
    }
    if (typeKey == "value-array" && toReturn[newTermKey]) {
      toReturn[newTermKey].push([queryKey]);
    }
    if (typeKey == "value-array" && !toReturn[newTermKey]) {
      toReturn[newTermKey] = [queryKey];
    }
    if (typeKey == "options") {
      let new_key = newTermKey;
      let new_value = new_key == "sort" ? "" : [];
      if (Array.isArray(queryKey) == false) {
        queryKey = [queryKey];
      }
      queryKey.forEach((old_term) => {
        if (Array.isArray(new_value)) {
          new_value.push(urlMap[key]["options"][String(old_term)]);
        } else {
          new_value += urlMap[key]["options"][String(old_term)];
        }
      });
      toReturn[new_key] = new_value;
    }
  }
  return toReturn;
}

/**
 * Whether the hash is valid or not
 *
 * @param {String} hash
 *
 * @returns {undefined|String}
 */
export function getValidHash(hash) {
  if (hash == undefined || hash == "") {
    return undefined;
  }
  if (hash == "#") {
    return hash;
  }

  // remove the :~: text search hash
  if (hash.startsWith(`#${BOOKMARK_PREFIX}`)) {
    return undefined;
  }
  if (hash.indexOf(BOOKMARK_PREFIX) > 1) {
    hash = hash.substring(0, hash.indexOf(BOOKMARK_PREFIX));
  }
  return hash;
}
